import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ShoppingBag } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { ShopOrder } from '@shared/sdk-v1';

const OrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const courier = useSelector(getCourier);
  const [order, setOrder] = useState<ShopOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) return;
      try {
        const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:9001/api').replace(/\/+$/, '');
        const filter = encodeURIComponent(JSON.stringify({ include: 'items' }));
        const res = await fetch(`${apiBase}/ShopOrders/${orderId}?filter=${filter}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) throw new Error('Order not found');
        const data = await res.json();
        setOrder(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center space-y-4">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Order Not Found</h2>
        <p className="text-gray-500 dark:text-gray-400">{error || 'Unable to load order details.'}</p>
        <Link to="/shop" className="inline-block mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium min-h-[44px]">
          Back to Shop
        </Link>
      </div>
    );
  }

  const statusColor = order.paymentStatus === 'paid' 
    ? 'text-green-600 dark:text-green-400'
    : order.paymentStatus === 'pending_verification'
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-600 dark:text-gray-400';

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex items-start lg:items-center justify-center">
      <div className="w-full max-w-lg lg:max-w-xl mx-auto p-4 lg:p-8 space-y-6 pb-8">
        {/* Success Header */}
        <div className="text-center space-y-4 pt-6 lg:pt-0">
          {/* Animated checkmark */}
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute inset-0 bg-green-100 dark:bg-green-900/30 rounded-full animate-ping opacity-25 w-20 h-20 lg:w-24 lg:h-24" />
            <div className="relative bg-green-50 dark:bg-green-900/20 rounded-full p-4 lg:p-5">
              <CheckCircle className="h-12 w-12 lg:h-16 lg:w-16 text-green-500" />
            </div>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Order Placed!</h1>
          <p className="text-gray-500 dark:text-gray-400 lg:text-lg">
            Thank you for your order. We'll send updates to your email.
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 lg:p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900 dark:text-white lg:text-lg">Order #{order.id}</h3>
            <span className={`text-sm font-medium ${statusColor}`}>
              {order.paymentStatus === 'pending_verification' ? 'Payment Under Review' : (order.paymentStatus || 'Pending')}
            </span>
          </div>

          {order.items && order.items.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              {order.items.map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between text-sm lg:text-base lg:py-1">
                  <span className="text-gray-600 dark:text-gray-400">
                    {item.productName} × {item.quantity}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    JMD ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="font-semibold text-gray-900 dark:text-white">Total</span>
            <span className="font-bold text-lg lg:text-xl text-gray-900 dark:text-white">
              JMD ${order.totalAmount?.toFixed(2) || '0.00'}
            </span>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1 pt-2">
            <p><span className="font-medium">Payment:</span> {order.paymentMethod?.replace('_', ' ') || 'N/A'}</p>
            <p><span className="font-medium">Status:</span> {order.status || 'Pending'}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Link
            to="/shop"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium min-h-[44px] shadow-sm hover:bg-primary/90 transition-colors"
          >
            <ShoppingBag className="h-5 w-5" />
            Continue Shopping
          </Link>
          <Link
            to="/auth"
            className="w-full flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 
              text-gray-700 dark:text-gray-300 rounded-xl font-medium min-h-[44px] hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Create an Account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
